import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  fetchUnavailableServices,
  getAreaAvailability,
  OPEN_AVAILABILITY,
  subscribeAreaAvailability,
  type AreaAvailability,
} from '../services/areaAvailability';

/**
 * What is not offered at the address on screen - for any tile, anywhere.
 *
 * A reader only. The answer is written by the screen that knows the address
 * and the catalogue (`HomeScreen`, through `useUnavailableServices` below), and
 * every service row, service card and category tile reads it here to grey
 * itself out. See the store's note in `services/areaAvailability.ts` for why this
 * is not a React context.
 */
export function useAreaAvailability(): AreaAvailability {
  return useSyncExternalStore(
    subscribeAreaAvailability,
    getAreaAvailability,
    getAreaAvailability,
  );
}

type Unavailable = {
  enforced: boolean;
  services: ReadonlySet<string>;
  /** Outside every served area, with the gate on - see `fetchUnavailableServices`. */
  outsideAreas: boolean;
  /** Asks again for the same address. Resolves when the answer has landed. */
  reload: () => Promise<void>;
};

const NONE = {
  enforced: OPEN_AVAILABILITY.enforced,
  services: OPEN_AVAILABILITY.services,
  outsideAreas: false,
};

/**
 * Fetches the services not offered at one pin, and keeps the answer current.
 *
 * ---------------------------------------------------------------------------
 * A NEW ADDRESS STARTS FROM "NOTHING UNAVAILABLE"
 * ---------------------------------------------------------------------------
 * Changing the address clears the previous answer at once rather than keeping
 * it until the new one lands. Keeping it would leave the OLD address's greys
 * on the new one for a moment - and a "coming soon" on a service the new
 * address does offer blocks a tap somebody meant. A tile that greys a beat
 * late costs nothing; one that is wrongly grey costs the booking.
 *
 * `reload` does not clear. A pull-to-refresh on the same address should not
 * flash every tile back to normal and grey again.
 *
 * Keyed on the coordinates' values, not the object: the pin is rebuilt on many
 * renders with identical numbers, and depending on its identity would refetch
 * on every one of them.
 */
export function useUnavailableServices(
  pin: { lat: number; lng: number } | undefined,
  cityId: string | undefined,
): Unavailable {
  const [state, setState] = useState(NONE);
  const lat = pin?.lat;
  const lng = pin?.lng;

  /* The request in flight, so a newer one can abandon it. */
  const inflight = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    inflight.current?.abort();

    if (lat === undefined || lng === undefined) {
      setState(NONE);
      return;
    }

    const controller = new AbortController();
    inflight.current = controller;

    const next = await fetchUnavailableServices(
      { lat, lng },
      cityId,
      controller.signal,
    );

    /* Superseded while it was out - an address changed, or the screen left. */
    if (controller.signal.aborted) {
      return;
    }
    setState(next);
  }, [lat, lng, cityId]);

  useEffect(() => {
    setState(NONE);
    load().catch(() => undefined);
    return () => inflight.current?.abort();
  }, [load]);

  return {
    enforced: state.enforced,
    services: state.services,
    outsideAreas: state.outsideAreas,
    reload: load,
  };
}
